"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import type { NotificationFiltersState } from "@/features/notifications/shared/notificationFilters";
import {
  NOTIFICATIONS_SCROLL_PAGE_SIZE,
  scrollNotifications,
} from "@/lib/services/notificationsService";
import { QueryKeys } from "@/lib/types";
import { NOTIFICATIONS_REFETCH_INTERVAL_MS } from "@/features/notifications/constants";
import { useNotificationsRefetchInterval } from "./useNotificationsRefetchInterval";

interface UseNotificationsScrollParams {
  userId: number;
  page: number;
  pageSize?: number;
  lastSelectedId?: string;
  filters: NotificationFiltersState;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export function useNotificationsScroll({
  userId,
  page,
  pageSize = NOTIFICATIONS_SCROLL_PAGE_SIZE,
  lastSelectedId,
  filters,
  enabled = true,
  refetchInterval = NOTIFICATIONS_REFETCH_INTERVAL_MS,
}: UseNotificationsScrollParams) {
  const accessToken = useAccessToken();
  const effectiveRefetchInterval =
    useNotificationsRefetchInterval(refetchInterval);

  return useQuery({
    queryKey: [
      QueryKeys.Notifications,
      "scroll",
      userId,
      page,
      pageSize,
      lastSelectedId,
      filters,
    ],
    queryFn: () =>
      scrollNotifications(accessToken!, {
        userId,
        page,
        pageSize,
        lastSelectedId,
        filters,
      }),
    enabled: !!accessToken && !!userId && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 30 * 1000,
    refetchInterval: effectiveRefetchInterval,
    placeholderData: (previousData) => previousData,
  });
}
